<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<!-- LIST VIEW MODE                                                              -->
<!-- ═══════════════════════════════════════════════════════════════════════════ -->

<div *ngIf="viewMode === 'list'" class="container-fluid">

  <div class="table-container">

    <!-- Card Header -->
    <div class="page-header">
      <div class="header-content">
        <h2 class="page-title">List of Documents</h2>
        <div class="header-actions">
          <div class="search-wrapper">
            <input
              type="text"
              class="search-input"
              [(ngModel)]="searchText"
              (input)="onSearchInput($event)"
              placeholder="Search Document">
            <img src="assets/img/search.svg" class="search-icon" alt="Search">
          </div>
          <button class="btn-create" (click)="newDocument()">
            <i class="fa fa-plus me-2"></i> Create Document
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div *ngIf="filteredDocuments.length > 0" class="table-responsive-wrapper">
      <table class="head-table">
        <thead>
          <tr>
            <th class="col-doc-name">
              <div class="th-content">
                <span>Document Name</span>
                <img src="assets/img/SORT.PNG" class="sort-icon" (click)="sortingFile('documentname')" alt="Sort">
              </div>
            </th>
            <th class="col-created-date">
              <div class="th-content">
                <span>Created Date</span>
                <img src="assets/img/SORT.PNG" class="sort-icon" (click)="sortingDateFile('updatedon?.$date')" alt="Sort">
              </div>
            </th>
            <th class="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr class="table-row" *ngFor="let doc of pagedDocuments">
            <td class="col-doc-name" [title]="doc.documentname">{{ doc.documentname }}</td>
            <td class="col-created-date">{{ doc.updatedon?.$date | date:'MMM dd, yyyy' }}</td>
            <td class="action-cell col-actions">
              <button
                mat-icon-button
                class="action-btn"
                [matMenuTriggerFor]="docMenu"
                aria-label="Document actions">
                <span class="action-dots">⋮</span>
              </button>
              <mat-menu #docMenu="matMenu" class="custom-mat-menu">
                <button mat-menu-item (click)="viewDocumentModal(doc)">View</button>
                <button mat-menu-item (click)="editDocument(doc)">Edit</button>
                <button mat-menu-item (click)="deleteDocumentClick(doc)">Delete</button>
              </mat-menu>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div *ngIf="filteredDocuments.length > 0" class="nav-buttons">
      <button (click)="prevPage()" [disabled]="!canGoToPrevious" class="btn btn-primary">
        &lt;&lt; Prev
      </button>
      <button (click)="nextPage()" [disabled]="!canGoToNext" class="btn btn-primary">
        Next &gt;&gt;
      </button>
    </div>

    <!-- Empty State -->
    <div *ngIf="filteredDocuments.length === 0" class="empty-state-container">
      <!-- A search is active but nothing matched — the documents still exist,
           so show "No results found" rather than the first-run empty state. -->
      <ng-container *ngIf="searchText?.trim(); else noDocsYet">
        <img src="assets/img/e-matter.png" class="empty-state-icon" alt="No results">
        <p class="empty-state-title">No results found</p>
        <p class="empty-state-subtitle">No documents match your search. Try a different keyword.</p>
      </ng-container>
      <ng-template #noDocsYet>
        <img src="assets/img/e-matter.png" class="empty-state-icon" alt="No documents">
        <p class="empty-state-title">No Documents Yet!</p>
        <p class="empty-state-subtitle">Create and organize your documents by getting started.</p>
        <button class="btn-create" (click)="newDocument()">
          <i class="fa fa-plus me-2"></i> Create Document
        </button>
      </ng-template>
    </div>

  </div>

</div>

<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<!-- EDITOR VIEW MODE                                                            -->
<!-- ═══════════════════════════════════════════════════════════════════════════ -->

<div *ngIf="viewMode === 'editor'" class="editor-shell">

  <!-- Sticky top bar: document title row + a single shared toolbar row that
       holds BOTH the editor's formatting toolbar (left) and the preview's
       hamburger/page-nav/zoom controls (right). Because both live in the
       same row, the editor page canvas and the preview page canvas below
       start scrolling from the exact same y-position — there's no separate
       "Preview" header sitting on top of just one side anymore. -->
  <div class="editor-topbar">

    <!-- Document Title Bar -->
    <div class="doc-title-bar">
      <button class="back-btn" (click)="backToList()" title="Back to list">
        <i class="fa fa-arrow-left"></i>
      </button>

      <div class="doc-title-group">
        <span
          *ngIf="!editingDocName"
          class="doc-name"
          (click)="startEditDocName()"
          title="Click to rename">
          {{ documentname || 'Untitled document' }}
        </span>
        <input
          *ngIf="editingDocName"
          #docNameInput
          class="doc-name-input"
          [(ngModel)]="documentname"
          maxlength="255"
          (input)="restrictMetaInput($event)"
          (blur)="finishEditDocName()"
          (keydown.enter)="finishEditDocName()" />

        <span class="save-status" [ngClass]="'save-status--' + saveStatus" *ngIf="saveStatus !== 'idle'">
          {{ saveStatusLabel }}
        </span>
      </div>

      <div class="doc-title-actions">
        <button class="menu-btn menu-btn--ghost" (click)="previewCurrentDoc()" [disabled]="isSaving" title="Preview compiled PDF">
          <i class="fa fa-file-pdf-o"></i> Preview PDF
        </button>
        <button class="menu-btn menu-btn--primary" (click)="saveDocument()" [disabled]="isSaving" title="Save document">
          <i class="fa fa-save"></i> Save
        </button>
      </div>
    </div>

    <!-- Shared toolbar row: editor formatting toolbar (left) + preview
         controls (right), side by side in one row. -->
    <div class="toolbar-row">

      <!-- Formatting Toolbar -->
      <div class="tiptap-toolbar" *ngIf="editor">

        <!-- Heading / format dropdown -->
        <button
          class="tb-btn tb-dropdown-btn"
          mat-button
          #menuTrigger1="matMenuTrigger"
          [matMenuTriggerFor]="menuHeading"
          title="Text style">
          {{ getHeadingLabel() }} <i class="fa fa-angle-down tb-caret"></i>
        </button>
        <mat-menu #menuHeading="matMenu">
          <button mat-menu-item [class.active]="editor.isActive('paragraph')" (click)="setParagraph()">Normal text</button>
          <button mat-menu-item [class.active]="editor.isActive('docTitle')" (click)="setTitle()">Title</button>
          <button mat-menu-item [class.active]="editor.isActive('heading', {level: 1})" (click)="setHeading(1)">Section</button>
          <button mat-menu-item [class.active]="editor.isActive('heading', {level: 2})" (click)="setHeading(2)">Sub Section</button>
        </mat-menu>

        <!-- Font size dropdown -->
        <button
          class="tb-btn tb-dropdown-btn tb-fontsize-btn"
          mat-button
          #menuTrigger3="matMenuTrigger"
          [matMenuTriggerFor]="menuFontSize"
          title="Font size">
          {{ getFontSize() }} <i class="fa fa-angle-down tb-caret"></i>
        </button>
        <mat-menu #menuFontSize="matMenu">
          <button mat-menu-item *ngFor="let size of fontSizes" (click)="setFontSize(size)">{{ size.replace('px','') }}</button>
        </mat-menu>

        <span class="tb-divider"></span>

        <!-- Inline formatting -->
        <button class="tb-btn" [class.active]="editor.isActive('bold')"
          (click)="editor.chain().focus().toggleBold().run()" title="Bold (Ctrl+B)">
          <b>B</b>
        </button>
        <button class="tb-btn" [class.active]="editor.isActive('italic')"
          (click)="editor.chain().focus().toggleItalic().run()" title="Italic (Ctrl+I)">
          <i>I</i>
        </button>
        <button class="tb-btn" [class.active]="editor.isActive('underline')"
          (click)="editor.chain().focus().toggleUnderline().run()" title="Underline (Ctrl+U)">
          <u>U</u>
        </button>

        <!-- Text color -->
        <button class="tb-btn tb-color-btn" type="button"
          #colorMenuTrigger="matMenuTrigger" [matMenuTriggerFor]="menuColor" title="Text color">
          <span class="tb-color-glyph" [style.border-bottom-color]="getActiveColor()">A</span>
          <i class="fa fa-angle-down tb-caret"></i>
        </button>
        <mat-menu #menuColor="matMenu" class="tb-color-menu">
          <div class="tb-color-panel" (click)="$event.stopPropagation()">
            <div class="tb-color-grid">
              <ng-container *ngFor="let row of textColorPalette">
                <button type="button" *ngFor="let c of row" class="tb-color-swatch"
                  [class.is-light]="c === '#ffffff' || c === '#f3f3f3'"
                  [style.background-color]="c" [title]="c"
                  (click)="applyColor(c); colorMenuTrigger.closeMenu()"></button>
              </ng-container>
            </div>

            <div class="tb-color-custom-label">CUSTOM</div>
            <div class="tb-color-custom-row">
              <button type="button" *ngFor="let c of recentColors" class="tb-color-swatch"
                [style.background-color]="c" [title]="c"
                (click)="applyColor(c); colorMenuTrigger.closeMenu()"></button>
              <label class="tb-color-add" title="Custom color">
                <i class="fa fa-plus"></i>
                <input type="color" (input)="applyCustomColor($event)"
                       (click)="$event.stopPropagation()" />
              </label>
            </div>
          </div>
        </mat-menu>

        <span class="tb-divider"></span>

        <!-- Alignment -->
        <button class="tb-btn" [class.active]="isAlignActive('left')"
          (click)="setAlign('left')" title="Align left">
          <i class="fa fa-align-left"></i>
        </button>
        <button class="tb-btn" [class.active]="isAlignActive('center')"
          (click)="setAlign('center')" title="Align center">
          <i class="fa fa-align-center"></i>
        </button>
        <button class="tb-btn" [class.active]="isAlignActive('right')"
          (click)="setAlign('right')" title="Align right">
          <i class="fa fa-align-right"></i>
        </button>
        <button class="tb-btn" [class.active]="isAlignActive('justify')"
          (click)="setAlign('justify')" title="Justify">
          <i class="fa fa-align-justify"></i>
        </button>

        <span class="tb-divider"></span>

        <!-- Lists -->
        <button class="tb-btn" [class.active]="editor.isActive('bulletList')"
          (click)="setListType('bulletList')" title="Bulleted List">
          <img src="assets/img/unord.svg" class="tb-icon" alt="Bullet list"
               onerror="this.style.display='none';this.parentElement.innerHTML='• List'"/>
        </button>
        <button class="tb-btn" [class.active]="editor.isActive('orderedList')"
          (click)="setListType('orderedList')" title="Numbered List">
          <img src="assets/img/order.svg" class="tb-icon" alt="Ordered list"
               onerror="this.style.display='none';this.parentElement.innerHTML='1. List'"/>
        </button>

        <span class="tb-divider"></span>

        <!-- Insert menu: Table, Page Break, Link -->
        <button
          #insertMenuBtn
          class="tb-btn tb-dropdown-btn"
          mat-button
          #menuTriggerInsert="matMenuTrigger"
          [matMenuTriggerFor]="menuInsert"
          (mousedown)="captureSelection()"
          title="Insert">
          Insert <i class="fa fa-angle-down tb-caret"></i>
        </button>
        <mat-menu #menuInsert="matMenu">
          <button mat-menu-item (click)="openTablePicker()">
            <i class="fa fa-table tb-menu-icon"></i> Table
          </button>
          <button mat-menu-item (click)="insertPageBreak()">
            <i class="fa fa-scissors tb-menu-icon"></i> Page Break
          </button>
          <button mat-menu-item (click)="insertLink()">
            <i class="fa fa-link tb-menu-icon"></i> Link
          </button>
        </mat-menu>

        <!-- Contextual Table Actions -->
        <ng-container *ngIf="editor.isActive('table')">
          <button class="tb-btn" (click)="addTableRow()" title="Add Row">+Row</button>
          <button class="tb-btn" (click)="addTableCol()" title="Add Column">+Col</button>
          <button class="tb-btn tb-danger" (click)="deleteTable()" title="Delete Table">Del Table</button>
        </ng-container>

        <span class="tb-divider"></span>

        <!-- Undo / Redo -->
        <button
          class="tb-btn tb-icon-btn"
          [disabled]="!editor.can().undo()"
          (click)="editor.chain().focus().undo().run()"
          title="Undo (Ctrl+Z)">
          <svg viewBox="0 0 24 24" class="tb-svg-icon" aria-hidden="true">
            <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
          </svg>
        </button>
        <button
          class="tb-btn tb-icon-btn"
          [disabled]="!editor.can().redo()"
          (click)="editor.chain().focus().redo().run()"
          title="Redo (Ctrl+Y)">
          <svg viewBox="0 0 24 24" class="tb-svg-icon" aria-hidden="true">
            <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>
          </svg>
        </button>

      </div>

      <!-- Preview controls — split into two balanced groups within the row:
           left = hamburger (thumbnails) + "Preview" label, right = page nav
           + zoom. Both groups live in .preview-toolbar-controls, which spans
           the full width of the preview column (matching .preview-panel's
           flex-basis below), so the left group sits flush at the preview
           column's left edge and the right group sits flush at its right
           edge — same balance as the editor toolbar's own left-aligned
           layout, on the same row. -->
      <div class="preview-toolbar-controls">

        <div class="preview-toolbar-left">
          <button
            class="pv-hamburger-btn"
            [class.pv-hamburger-btn--active]="showThumbnailPanel"
            (click)="toggleThumbnailPanel()"
            [disabled]="!showPreviewDoc"
            title="Show page thumbnails">
            <svg viewBox="0 0 24 24" class="pv-hamburger-icon" aria-hidden="true">
              <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
            </svg>
          </button>

          <span class="preview-label">Preview</span>
        </div>

        <!-- PDF page navigation + zoom controls -->
        <div class="preview-controls" *ngIf="showPreviewDoc">
          <button class="pv-btn" (click)="goToPdfPage(-1)" [disabled]="pdfCurrentPage <= 1" title="Previous page">
            <i class="fa fa-chevron-left"></i>
          </button>

          <div class="pv-page-nav" title="Go to page">
            <span
              *ngIf="!pdfPageEditing"
              class="pv-page-display"
              [class.pv-page-display--editable]="pdfTotalPages > 1"
              (click)="startEditPdfPage()">
              {{ pdfCurrentPage }} of {{ pdfTotalPages }}
            </span>
            <input
              *ngIf="pdfPageEditing"
              #pdfPageInput
              type="number"
              class="pv-page-input"
              [value]="pdfCurrentPage"
              min="1"
              [max]="pdfTotalPages"
              (blur)="commitPdfPageEdit($event.target)"
              (keydown.enter)="commitPdfPageEdit($event.target)"
              (keydown.escape)="pdfPageEditing = false" />
          </div>

          <button class="pv-btn" (click)="goToPdfPage(1)" [disabled]="pdfCurrentPage >= pdfTotalPages" title="Next page">
            <i class="fa fa-chevron-right"></i>
          </button>

          <span class="pv-divider"></span>


          <button class="pv-btn" (click)="setPdfZoom(-10)" [disabled]="pdfZoom <= 50" title="Zoom out">
            <i class="fa fa-minus"></i>
          </button>
          <span class="pv-zoom-indicator">{{ pdfZoom }}%</span>
          <button class="pv-btn" (click)="setPdfZoom(10)" [disabled]="pdfZoom >= 300" title="Zoom in">
            <i class="fa fa-plus"></i>
          </button>
        </div>
      </div>

    </div>

  </div>

  <!-- Body: editor canvas + preview panel -->
  <div class="editor-body">

    <!-- Scrollable gray canvas with the centered white page (the single editing surface) -->
    <div #canvasWrap class="tiptap-canvas-wrap">
      <div class="tiptap-page-shadow">
        <div #editorEl class="tiptap-editor"></div>
      </div>
    </div>

    <!-- RIGHT: compiled PDF preview panel (editor left, preview right).
         No header here anymore — its controls now live in .toolbar-row
         above, so this panel is just the thumbnail sidebar + page canvas. -->
    <div class="preview-panel">

      <!-- Body: thumbnail sidebar + main canvas -->
      <div class="preview-body">

        <!-- Thumbnail sidebar -->
        <div class="preview-thumb-sidebar" *ngIf="showThumbnailPanel">
          <div class="preview-thumb-sidebar-header">Pages</div>
          <div class="preview-thumb-list">
            <button
              *ngFor="let thumb of pdfThumbnails"
              type="button"
              class="preview-thumb-item"
              [class.preview-thumb-item--active]="thumb.pageNumber === pdfCurrentPage"
              (click)="goToPageFromThumbnail(thumb.pageNumber)">
              <img [src]="thumb.dataUrl" class="preview-thumb-img" [alt]="'Page ' + thumb.pageNumber" />
              <span class="preview-thumb-pagenum">{{ thumb.pageNumber }}</span>
            </button>
            <div
              *ngFor="let _ of pendingThumbnailSlots"
              class="preview-thumb-item preview-thumb-item--placeholder">
            </div>
          </div>
        </div>

        <!-- Main page canvas -->
        <div class="preview-frame-wrap" #previewFrameWrap>
          <div class="preview-canvas-scroll" #previewCanvasScroll *ngIf="showPreviewDoc">
            <!-- Page wrapper positions the pdf.js annotation (link) layer exactly
                 over the rendered canvas so hyperlinks in the compiled PDF are
                 clickable and open their destination in a new tab. -->
            <div class="preview-page" #previewPage>
              <canvas #pdfCanvas class="preview-pdf-canvas"></canvas>
              <div class="annotationLayer" #annotationLayer></div>
            </div>
          </div>
          <div class="preview-empty" *ngIf="!showPreviewDoc">
            <div class="preview-empty-inner">
              <span class="preview-empty-icon">📄</span>
              <p>Click Preview PDF to compile.</p>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>

  <!-- Hidden Image Upload Input -->
  <input
    #imageFileInput
    type="file"
    accept="image/jpeg,image/png"
    (change)="handleImageUpload($event)"
    style="display:none" />

  <!-- ─── Table grid picker (Google-Docs style Insert > Table) ─── -->
  <div #tablePickerEl class="tb-grid-picker" *ngIf="showTablePicker"
       [style.top.px]="tablePickerPos.top" [style.left.px]="tablePickerPos.left"
       (mousedown)="$event.stopPropagation()">
    <div class="tb-grid-picker-grid" (mouseleave)="onTablePickerLeaveGrid()">
      <div class="tb-grid-picker-row" *ngFor="let r of tablePickerRowIndices">
        <div class="tb-grid-picker-cell"
             *ngFor="let c of tablePickerColIndices"
             [class.tb-grid-picker-cell--active]="r <= tablePickerHoverRow && c <= tablePickerHoverCol"
             (mouseenter)="onTablePickerHover(r, c)"
             (click)="onTablePickerPick(r, c)">
        </div>
      </div>
    </div>
    <div class="tb-grid-picker-label">{{ tablePickerLabel }}</div>
  </div>

  <!-- ─── Link insertion/editing popover ─── -->
  <div #linkPopoverEl class="tb-link-popover" *ngIf="showLinkPopover"
       [style.top.px]="linkPopoverPos.top" [style.left.px]="linkPopoverPos.left"
       (mousedown)="$event.stopPropagation()"
       (keydown.enter)="applyLinkPopover()" (keydown.escape)="closeLinkPopover()">
    <input #linkUrlInput type="text" class="tb-link-popover-input"
           placeholder="Paste or type a URL" [(ngModel)]="linkPopoverUrl" />
    <button *ngIf="linkPopoverMode === 'edit'" class="tb-link-popover-btn tb-link-popover-btn--open"
            [disabled]="!linkPopoverUrl.trim()" (click)="openLinkPopoverUrl()" title="Open link">
      <i class="fa fa-external-link"></i>
    </button>
    <button class="tb-link-popover-btn tb-link-popover-btn--apply" (click)="applyLinkPopover()" title="Apply">
      <i class="fa fa-check"></i>
    </button>
    <button *ngIf="linkPopoverMode === 'edit'" class="tb-link-popover-btn tb-link-popover-btn--remove"
            (click)="removeLinkPopover()" title="Remove link">
      <i class="fa fa-unlink"></i>
    </button>
  </div>

</div>

<!-- ─── Document/PDF Preview Modal (shared by list "View" and editor "Preview PDF") ─── -->
<div class="modal" tabindex="-1" *ngIf="showViewModal"
     style="display: block;"
     (click)="onModalBackdropClick($event)">
  <div class="modal-dialog modal-lg modal-dialog-centered" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Document Preview</h5>
        <button type="button" class="btn-close" (click)="closeViewModal()" aria-label="Close"></button>
      </div>
      <div class="modal-body modal-doc-body">
        <div *ngIf="!pdfSrc" class="loading-placeholder">
          <div class="loading-spinner"></div>
          <p>Loading document...</p>
        </div>
        <iframe
          *ngIf="pdfSrc"
          [src]="pdfSrc"
          frameborder="0"
          title="Document">
        </iframe>
      </div>
    </div>
  </div>
</div>

<!-- Loading Spinner -->
<ngx-spinner size="medium" type="ball-clip-rotate"></ngx-spinner>
<router-outlet></router-outlet>